import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export type OnboardingStep = 'business_description' | 'email' | 'connections' | 'contacts' | 'billing' | 'tour';
export type OnboardingStepStatus = 'pending' | 'completed' | 'skipped';

export interface OnboardingStepState {
  step: OnboardingStep;
  status: OnboardingStepStatus;
  completedAt: string | null;
}

export function isOnboardingComplete(progress: OnboardingStepState[] | undefined): boolean {
  if (!progress) return true; // sin datos todavía — nunca forzar la pantalla mientras carga
  return progress.every(s => s.status === 'completed' || s.status === 'skipped');
}

export function useOnboardingProgress(enabled = true) {
  return useQuery<{ progress: OnboardingStepState[] }>({
    queryKey: ['onboarding-progress'],
    queryFn: () => api.get<{ progress: OnboardingStepState[] }>('/api/onboarding/progress'),
    enabled,
    // Polling liviano mientras la inducción sigue abierta — los pasos avanzan
    // por chat con Evo Brain (tools) o por otra pestaña (Composio), no solo
    // por esta misma pantalla, así que un refetch pasivo importa acá más que
    // en el resto de la app.
    refetchInterval: (query) => (isOnboardingComplete(query.state.data?.progress) ? false : 4000),
  });
}

export type ConnectableToolkit = 'instagram' | 'facebook' | 'googlecalendar';

export interface ConnectionStatus {
  toolkit: ConnectableToolkit;
  connected: boolean;
  connectedAt: string | null;
}

export function useComposioConnections() {
  return useQuery<{ statuses: ConnectionStatus[] }>({
    queryKey: ['onboarding-connections'],
    queryFn: () => api.get<{ statuses: ConnectionStatus[] }>('/api/onboarding/connections'),
    refetchOnWindowFocus: true, // el cliente autoriza en OTRA pestaña y vuelve acá
  });
}

export function useStartComposioConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (toolkit: ConnectableToolkit) =>
      api.post<{ toolkit: string; redirectUrl: string }>(`/api/onboarding/connections/${toolkit}/start`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['onboarding-connections'] }),
  });
}
